import React, { useState, useRef, useEffect } from 'react';
import Dropdown from './Dropdown';
import { A, modalInputStyle as iS, btnCancel as bCancel, btnSave as bSave } from './theme';

function MemoryForm({ api, memory, onClose, onSaved }) {
  const [form, setForm] = useState({ key:memory?.key||'', value:memory?.value||'', category:memory?.category||'personal', group:memory?.group||'' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const dialogRef = useRef(null);
  const set = (k,v) => setForm(p=>({...p,[k]:v}));

  // Stable ref for onClose to avoid re-registering focus trap
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Focus trap
  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    const focusable = () => el.querySelectorAll('input,textarea,button,select,[tabindex]:not([tabindex="-1"])');
    const items = focusable();
    if (items.length) items[0].focus();
    const handleKeyDown = e => {
      if (e.key === 'Escape') { onCloseRef.current(); return; }
      if (e.key !== 'Tab') return;
      const els = focusable();
      if (!els.length) return;
      const first = els[0], last = els[els.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    el.addEventListener('keydown', handleKeyDown);
    return () => el.removeEventListener('keydown', handleKeyDown);
  }, []); // eslint-disable-line

  const save = async () => {
    if (!form.key.trim()||!form.value.trim()) { setError('Key and value required'); return; }
    setSaving(true); setError('');
    try {
      const url = memory ? `${api}/dashboard/memories/${memory.id}` : `${api}/dashboard/memories`;
      const res = await fetch(url, { method:memory?'PUT':'POST', headers:{'Content-Type':'application/json'},
        body:JSON.stringify({ key:form.key.trim(), value:form.value.trim(), category:form.category, group:form.group.trim()||null }) });
      if (!res.ok) { const d = await res.json(); setError(d.error||'Failed'); setSaving(false); return; }
      onSaved();
    } catch(e) { setError(e.message); }
    setSaving(false);
  };

  return (
    <div style={{ position:'fixed',inset:0,background:'rgba(0,0,0,0.8)',display:'flex',justifyContent:'center',alignItems:'center',zIndex:1000 }} onClick={onClose} onKeyDown={e=>e.key==='Escape'&&onClose()}>
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-labelledby="memory-form-title" style={{
        background:A.surfaceModal, borderRadius:'16px', padding:'24px', width:'400px', maxWidth:'92vw',
        border:`1px solid ${A.border}`, boxShadow:`0 0 40px rgba(123,104,238,0.1)`,
        position:'relative', animation:'fsu 0.3s ease-out',
        maxHeight:'85vh', overflowY:'auto',
      }} onClick={e=>e.stopPropagation()}>
        <div style={{ position:'absolute',top:0,left:'15%',right:'15%',height:'1px',background:`linear-gradient(90deg,transparent,${A.primary},${A.accent},transparent)` }} />

        <div style={{ display:'flex',alignItems:'center',gap:'8px',marginBottom:'18px' }}>
          <div style={{ width:'8px',height:'8px',borderRadius:'2px',background:A.accent,boxShadow:`0 0 8px ${A.accent}` }} />
          <h3 id="memory-form-title" style={{ margin:0,color:A.text,fontSize:'14px',fontWeight:700,letterSpacing:'2px',textTransform:'uppercase' }}>
            {memory?'Update Record':'New Record'}
          </h3>
        </div>

        <div style={{ display:'flex',flexDirection:'column',gap:'8px' }}>
          <input value={form.group} onChange={e=>set('group',e.target.value)} placeholder='Group (e.g. "Gmail Account")' aria-label="Memory group" style={iS} autoFocus />
          <input value={form.key} onChange={e=>set('key',e.target.value)} placeholder="Key (e.g. email, password) *" aria-label="Memory key" style={iS} />
          <textarea value={form.value} onChange={e=>set('value',e.target.value)} placeholder="Value *" aria-label="Memory value" rows={2} style={{...iS,resize:'none',fontFamily:'inherit'}} />
          <Dropdown value={form.category} onChange={v=>set('category',v)} aria-label="Memory category"
            options={['personal','contact','credentials','academic','other'].map(c=>({value:c,label:c}))} />
        </div>

        {error&&<p role="alert" style={{ color:'#ef4444',fontSize:'11px',margin:'8px 0 0' }}>{error}</p>}

        <div style={{ display:'flex',gap:'6px',marginTop:'16px',justifyContent:'flex-end' }}>
          <button onClick={onClose} style={bCancel}>ABORT</button>
          <button onClick={save} disabled={saving} style={bSave}>{saving?'STORING...':'STORE'}</button>
        </div>
      </div>
    </div>
  );
}

/* Styles imported from theme.js */

export default MemoryForm;
